export const resources = {
  common: {
    appName: 'Enrichment Ops',
    appTagline: 'really-enrich pipeline health, cost & coverage',
    loading: 'Loading...',
    loadError: 'Something went wrong loading this data.',
  },
  navigation: {
    overview: 'Overview',
    runs: 'Runs',
    enrichments: 'Enrichments',
    cost: 'Cost',
    latency: 'Latency',
    coverage: 'Coverage',
    logout: 'Sign out',
  },
  login: {
    title: 'Enrichment Ops',
    tagline: 'really-enrich pipeline health, cost & coverage',
    signIn: 'Sign in',
    signInLoading: 'Redirecting...',
    legalNote: "By signing in, you agree to Axetay's Terms of Service and Privacy Policy.",
  },
  pages: {
    overview: {
      title: 'Overview',
      subtitle: 'Health, cost and throughput across every enrichment scope',
    },
    runs: {
      title: 'Runs',
      subtitle: 'Search and triage the enrichment ledger by date, scope, property, status and cost',
    },
    enrichments: {
      title: 'Enrichments',
      subtitle: 'The current stored record for every property/scope, filterable by date, scope and outcome',
    },
    cost: {
      title: 'Cost',
      subtitle: 'AI spend by scope, trended over the last 14 days',
      trendTitle: 'Total daily cost · last 14 days',
      trendSubtitle: 'All scopes, AI enrichers only',
      rankingTitle: 'Cost by scope · today',
      loadError: 'Failed to load cost data.',
      emptyRanking: 'No cost recorded in the last 24 hours.',
    },
    latency: {
      title: 'Latency',
      subtitle: 'Run duration percentiles by scope',
      chartTitle: 'Run duration by scope · p50 / p95',
      chartSubtitle: 'Bar scaled to p95 · white tick marks p50',
      loadError: 'Could not load latency stats.',
      empty: 'No latency data for this window.',
    },
    coverage: {
      title: 'Coverage',
      subtitle: 'Enrichment outcome mix across the property catalog',
    },
  },
  comingSoon: (screen: string) => `${screen} is coming soon.`,
  runs: {
    windowLabel: 'Window',
    customRangeLabel: 'Custom range',
    customFromLabel: 'From',
    customToLabel: 'To',
    customRangeSeparator: '–',
    statusLabel: 'Status',
    outcomeLabel: 'Outcome',
    costBandLabel: 'Cost band',
    scopeLabel: 'Scope',
    propertyIdLabel: 'Property ID',
    propertyIdPlaceholder: 'e.g. 148502937',
    allScopes: 'All scopes',
    clearFilters: 'Clear filters',
    noResults: 'No runs match the current filters.',
    loadError: 'Failed to load runs. Try again shortly.',
    logsModalTitle: 'Run logs',
    noLogs: 'No log messages recorded for this run.',
    viewLogs: 'View',
    resultModalTitle: 'Result / error',
    columns: {
      runId: 'Run ID',
      scope: 'Scope',
      propertyId: 'Property ID',
      status: 'Status / Outcome',
      resultError: 'Result / error',
      duration: 'Duration',
      cost: 'Cost',
      model: 'Model',
      created: 'Created',
      logs: 'Logs',
    },
  },
  enrichments: {
    windowLabel: 'Window',
    customRangeLabel: 'Custom range',
    customFromLabel: 'From',
    customToLabel: 'To',
    customRangeSeparator: '–',
    outcomeLabel: 'Outcome',
    unreachableOutcomeTooltip:
      "Only ever produced by a run, never written to a stored enrichment record — see the Runs screen instead.",
    scopeLabel: 'Scope',
    propertyIdLabel: 'Property ID',
    propertyIdPlaceholder: 'e.g. 148502937',
    allScopes: 'All scopes',
    clearFilters: 'Clear filters',
    noResults: 'No enrichments match the current filters.',
    loadError: 'Failed to load enrichments. Try again shortly.',
    viewRuns: 'View runs',
    runsModalTitle: 'Contributing runs',
    runsModalSubtitle: 'Every run recorded for this property/scope, most recent first.',
    noRuns: 'No runs recorded for this property/scope.',
    runsLoadError: 'Failed to load runs for this enrichment.',
    unpublished: 'Unpublished',
    columns: {
      propertyId: 'Property ID',
      scope: 'Scope',
      outcome: 'Outcome',
      lastUpdated: 'Last updated',
      published: 'Published',
      runs: 'Runs',
    },
  },
  runDetail: {
    backToRuns: '← Back to Runs',
    title: 'Enrichment detail',
    loading: 'Loading enrichment record...',
    loadError: 'Failed to load the enrichment record.',
    noRecord:
      "No enrichment record is stored for this property/scope -- every run so far has failed or been skipped (only a successful run writes a record).",
    staleWarning:
      'A newer run has updated this record since this run -- showing the latest stored enrichment, not necessarily what this specific run produced.',
    recordPanelTitle: 'Record',
    aiMetricsPanelTitle: 'AI metrics',
    lastRun: 'Last run',
    result: 'Result',
    published: 'Published',
    inputHash: 'Input hash',
    runId: 'Run ID',
    scope: 'Scope',
    model: 'Model',
    tokens: 'Tokens',
    cost: 'Cost',
    latency: 'Latency',
    attempts: 'Attempts',
    trace: 'Trace',
    generationId: 'Generation ID',
    resolvedInputToggle: 'Resolved input',
    promptToggle: 'Prompt sent',
    promptOmitted: 'Prompt sent — omitted (oversize)',
    providerMetadataToggle: 'Provider metadata',
    outputPanelTitle: 'Output',
  },
  runEntry: {
    backToRuns: '← Back to Runs',
    title: 'Run detail',
    loading: 'Loading run...',
    loadError: 'Failed to load this run.',
    notFound: 'No run found for this runId.',
    runPanelTitle: 'Run',
    runId: 'Run ID',
    trigger: 'Trigger',
    scope: 'Scope',
    propertyId: 'Property ID',
    status: 'Status',
    duration: 'Duration',
    created: 'Created',
    resultPanelTitle: 'Result / error',
    noResultOrError: 'No result or error recorded for this run.',
    aiMetricsPanelTitle: 'AI metrics',
    model: 'Model',
    tokens: 'Tokens',
    cost: 'Cost',
    generationId: 'Generation ID',
    logsPanelTitle: 'Logs',
    noLogs: 'No log messages recorded for this run.',
    viewEnrichmentRecord: 'View the current enrichment record',
  },
  coverage: {
    cardTitle: 'Outcome mix by scope',
    cardSubtitle: "Share of each scope's ingested properties by latest enrichment result",
    ingestedSuffix: 'ingested',
    neverRunLabel: 'Never run',
    loadError: 'Could not load coverage data.',
    footnote:
      "Percentages are shares of every property this scope has ever been triggered for (ingested), not just the ones with a successful record. 'Never run' covers properties still pending, sampled out, or failing every attempt.",
  },
  overview: {
    windowOptions: {
      today: 'Today',
      '7d': '7 days',
      '30d': '30 days',
      all: 'All time',
      custom: 'Custom',
    },
    customFromLabel: 'From',
    customToLabel: 'To',
    customRangeSeparator: '–',
    customRangePrompt: 'Pick a start date to see stats for this range.',
    stats: {
      totalCost: 'Total cost',
      totalRuns: 'Total runs',
      successRate: 'Overall success rate',
      attention: 'Scopes needing attention',
    },
    tile: {
      successSuffix: 'success',
      coveredSuffix: 'covered',
      coveredTooltip: 'Share of every property this scope has ever ingested that has a successful record (lifetime, not windowed).',
      runs: 'Runs',
      spend: 'Spend',
      avgLatency: 'Avg latency',
      lastRun: 'Last run',
      coverage: 'Coverage',
    },
    status: {
      good: 'Healthy',
      warning: 'Degraded',
      critical: 'Critical',
    },
    legend: {
      title: 'Legend',
      statusGood: 'Healthy ≥ 97% success',
      statusWarning: 'Degraded 90–97%',
      statusCritical: 'Critical < 90%',
      coveragePrefix: 'Coverage:',
    },
  },
};
